/**
 * DepositScreen Component
 * Screen for USDT deposits via TON blockchain
 */

import { useState, useEffect } from 'react';
import { api } from '../services/api';
import { useUserContext } from '../contexts';
import { useTranslation } from '../hooks/useTranslation';

interface DepositScreenProps {
  onBack: () => void;
}

interface DepositInfo {
  memo: string;
  walletAddress: string;
  minAmount: number;
}

interface DepositHistoryItem {
  id: string;
  amount: number;
  memo: string | null;
  status: string;
  createdAt: string;
  processedAt: string | null;
  order: {
    id: string;
    asset: {
      name: string;
    };
  } | null;
}

export function DepositScreen({ onBack }: DepositScreenProps) {
  const { user, refreshUser } = useUserContext();
  const { t } = useTranslation();
  const [depositInfo, setDepositInfo] = useState<DepositInfo | null>(null);
  const [history, setHistory] = useState<DepositHistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedMemo, setCopiedMemo] = useState(false);
  const [copiedWallet, setCopiedWallet] = useState(false);

  useEffect(() => {
    loadDepositInfo();
    loadHistory();
  }, []);

  const loadDepositInfo = async () => {
    try {
      const response = await api.getBalanceDepositMemo();
      setDepositInfo(response.data);
    } catch (error: any) {
      console.error('Failed to load deposit info:', error);
      setError(error.response?.data?.error?.message || 'Failed to load deposit information');
    }
  };

  const loadHistory = async () => {
    try {
      const response = await api.getDepositHistory();
      setHistory(response.data.deposits || []);
    } catch (error: any) {
      console.error('Failed to load deposit history:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = async (text: string, type: 'memo' | 'wallet') => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'memo') {
        setCopiedMemo(true);
        setTimeout(() => setCopiedMemo(false), 2000);
      } else {
        setCopiedWallet(true);
        setTimeout(() => setCopiedWallet(false), 2000);
      }
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return '#10b981';
      case 'pending': return '#f59e0b';
      case 'manual_review': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed': return t('deposit.status_completed');
      case 'pending': return t('deposit.status_pending');
      case 'manual_review': return t('deposit.status_review');
      default: return status;
    }
  };

  if (isLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#0a0e1a',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <div>{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#0a0e1a',
      color: '#fff',
      maxWidth: '480px',
      margin: '0 auto',
    }}>
      {/* Header */}
      <div style={{
        padding: '16px',
        background: 'linear-gradient(180deg, #1a2332 0%, #0a0e1a 100%)',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
      }}>
        <button
          onClick={onBack}
          style={{
            background: 'rgba(59, 130, 246, 0.2)',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            borderRadius: '10px',
            color: '#fff',
            fontSize: '18px',
            cursor: 'pointer',
            padding: '8px 14px',
            minWidth: '44px',
            minHeight: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          ←
        </button>
        <h1 style={{ margin: 0, fontSize: '22px', fontWeight: 700 }}>
          {t('deposit.title')}
        </h1>
      </div>

      <div style={{ padding: '16px' }}>
        {/* Current Balance */}
        <div style={{
          background: 'linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)',
          borderRadius: '16px',
          padding: '20px',
          marginBottom: '20px',
          border: '1px solid rgba(46, 125, 50, 0.3)',
        }}>
          <div style={{
            fontSize: '14px',
            color: 'rgba(255,255,255,0.8)',
            marginBottom: '8px',
          }}>
            {t('deposit.current_balance')}
          </div>
          <div style={{
            fontSize: '32px',
            fontWeight: 'bold',
            color: '#fff',
          }}>
            {(Number(user?.usdtBalance) || 0).toFixed(2)} USDT
          </div>
          <div style={{
            fontSize: '14px',
            color: 'rgba(255,255,255,0.6)',
          }}>
            ≈ ${(Number(user?.usdtBalance) || 0).toFixed(2)}
          </div>
        </div>

        {error && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '20px',
            color: '#ef4444',
          }}>
            {error}
          </div>
        )}

        {depositInfo && (
          <>
            {/* Deposit Instructions */}
            <div style={{
              background: 'rgba(10, 22, 40, 0.6)',
              borderRadius: '16px',
              padding: '20px',
              marginBottom: '20px',
              border: '1px solid rgba(59, 130, 246, 0.3)',
            }}>
              <h3 style={{
                margin: '0 0 16px 0',
                fontSize: '18px',
                fontWeight: 600,
                color: '#3b82f6',
              }}>
                {t('deposit.how_to_deposit')}
              </h3>
              
              <div style={{ marginBottom: '16px' }}>
                <div style={{
                  fontSize: '14px',
                  color: 'rgba(255,255,255,0.8)',
                  marginBottom: '8px',
                }}>
                  {t('deposit.step_1')}
                </div>
                <div style={{
                  background: 'rgba(0, 0, 0, 0.3)',
                  borderRadius: '8px',
                  padding: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                }}>
                  <div style={{
                    fontSize: '12px',
                    fontFamily: 'monospace',
                    color: '#fff',
                    wordBreak: 'break-all',
                    flex: 1,
                    marginRight: '12px',
                  }}>
                    {depositInfo.walletAddress}
                  </div>
                  <button
                    onClick={() => copyToClipboard(depositInfo.walletAddress, 'wallet')}
                    style={{
                      background: copiedWallet ? '#10b981' : '#3b82f6',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '6px 12px',
                      color: '#fff',
                      fontSize: '12px',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {copiedWallet ? t('deposit.copied') : t('deposit.copy')}
                  </button>
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <div style={{
                  fontSize: '14px',
                  color: 'rgba(255,255,255,0.8)',
                  marginBottom: '8px',
                }}>
                  {t('deposit.step_2')}
                </div>
                <div style={{
                  background: 'rgba(0, 0, 0, 0.3)',
                  borderRadius: '8px',
                  padding: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                }}>
                  <div style={{
                    fontSize: '16px',
                    fontFamily: 'monospace',
                    color: '#10b981',
                    fontWeight: 'bold',
                  }}>
                    {depositInfo.memo}
                  </div>
                  <button
                    onClick={() => copyToClipboard(depositInfo.memo, 'memo')}
                    style={{
                      background: copiedMemo ? '#10b981' : '#3b82f6',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '6px 12px',
                      color: '#fff',
                      fontSize: '12px',
                      cursor: 'pointer',
                    }}
                  >
                    {copiedMemo ? t('deposit.copied') : t('deposit.copy')}
                  </button>
                </div>
              </div>

              <div style={{
                background: 'rgba(16, 185, 129, 0.1)',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                borderRadius: '8px',
                padding: '12px',
                fontSize: '12px',
                color: 'rgba(255, 255, 255, 0.8)',
              }}>
                <div style={{ marginBottom: '4px' }}>
                  • Минимальное пополнение: {depositInfo.minAmount} USDT
                </div>
                <div style={{ marginBottom: '4px' }}>
                  • Время обработки: Обычно в течение 5-10 минут
                </div>
                <div>
                  • Сеть: TON (The Open Network)
                </div>
              </div>
            </div>

            {/* Refresh Button */}
            <button
              onClick={() => {
                refreshUser();
                loadHistory();
              }}
              style={{
                width: '100%',
                padding: '16px',
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                border: 'none',
                borderRadius: '12px',
                color: '#fff',
                fontSize: '16px',
                fontWeight: 600,
                cursor: 'pointer',
                marginBottom: '20px',
                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
              }}
            >
              {t('deposit.refresh_balance')}
            </button>
          </>
        )}

        {/* Deposit History */}
        {history.length > 0 && (
          <div style={{
            background: 'rgba(10, 22, 40, 0.6)',
            borderRadius: '16px',
            padding: '20px',
            border: '1px solid rgba(59, 130, 246, 0.3)',
          }}>
            <h3 style={{
              margin: '0 0 16px 0',
              fontSize: '18px',
              fontWeight: 600,
              color: '#3b82f6',
            }}>
              {t('deposit.deposit_history')}
            </h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {history.map((deposit) => (
                <div
                  key={deposit.id}
                  style={{
                    background: 'rgba(0, 0, 0, 0.3)',
                    borderRadius: '8px',
                    padding: '12px',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                  }}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '8px',
                  }}>
                    <div style={{
                      fontSize: '16px',
                      fontWeight: 600,
                      color: '#10b981',
                    }}>
                      +{deposit.amount.toFixed(2)} USDT
                    </div>
                    <div style={{
                      fontSize: '12px',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      background: getStatusColor(deposit.status),
                      color: '#fff',
                      fontWeight: 500,
                    }}>
                      {getStatusText(deposit.status)}
                    </div>
                  </div>
                  
                  <div style={{
                    fontSize: '12px',
                    color: 'rgba(255, 255, 255, 0.6)',
                  }}>
                    {new Date(deposit.createdAt).toLocaleString()}
                  </div>
                  
                  {deposit.memo && (
                    <div style={{
                      fontSize: '11px',
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontFamily: 'monospace',
                      marginTop: '4px',
                    }}>
                      Memo: {deposit.memo}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}